import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { Separator } from '@ordo/ui/separator'
import { useMergeContacts } from 'hooks/api'
import { useFetchApi } from 'hooks/useFetchApi'
import { X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ContactDetail, DuplicateGroup } from 'types/contacts'

import {
  AssociationMergeSection,
  buildFieldOverrides,
  collectAssociations,
  contactDisplayName,
  FieldSelector,
  initFieldSelectionsFor,
  MERGE_FIELDS,
  type MergeFieldKey,
} from './merge-shared'

export function DuplicateReviewDialog({
  duplicates,
  open,
  onOpenChange,
  onComplete,
}: {
  duplicates: DuplicateGroup[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: () => void
}) {
  const { fetchApi } = useFetchApi()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set())
  const [details, setDetails] = useState<ContactDetail[]>([])
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [fieldSelections, setFieldSelections] = useState<Record<MergeFieldKey, string>>(
    {} as Record<MergeFieldKey, string>,
  )
  const [excludedAssociationIds, setExcludedAssociationIds] = useState<Set<string>>(new Set())
  const { perform: mergeContacts, loading: merging } = useMergeContacts()

  const currentGroup = duplicates[currentIndex] ?? null

  // Active contacts = group contacts minus removed ones
  const activeContacts = useMemo(() => {
    if (!currentGroup) {
      return []
    }
    return currentGroup.contacts.filter((c) => !removedIds.has(c.id))
  }, [currentGroup, removedIds])

  // Active details = fetched details minus removed ones
  const activeDetails = useMemo(() => details.filter((d) => !removedIds.has(d.id)), [details, removedIds])

  // Target is always the first active contact
  const targetId = activeContacts[0]?.id ?? ''

  // Fetch details for current group's contacts
  const loadGroupDetails = useCallback(
    async (group: DuplicateGroup) => {
      setLoadingDetails(true)
      try {
        const fetched = await Promise.all(group.contacts.map((c) => fetchApi<ContactDetail>(`/api/contacts/${c.id}`)))
        setDetails(fetched)
        setFieldSelections(initFieldSelectionsFor(group.contacts[0]?.id ?? ''))
        setExcludedAssociationIds(new Set())
        setRemovedIds(new Set())
      } finally {
        setLoadingDetails(false)
      }
    },
    [fetchApi],
  )

  // Load details when group changes
  useEffect(() => {
    if (open && currentGroup) {
      loadGroupDetails(currentGroup)
    }
  }, [open, currentIndex, currentGroup, loadGroupDetails])

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentIndex(0)
    }
  }, [open])

  const allAssociations = useMemo(
    () => activeDetails.flatMap((c) => collectAssociations(c, contactDisplayName(c))),
    [activeDetails],
  )

  const toggleAssociation = useCallback((id: string) => {
    setExcludedAssociationIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const removeContact = useCallback(
    (id: string) => {
      setRemovedIds((prev) => {
        const next = new Set(prev)
        next.add(id)
        return next
      })
      // If the removed contact was the target in field selections, reset to first remaining
      setFieldSelections((prev) => {
        const remaining = activeContacts.filter((c) => c.id !== id)
        if (remaining.length === 0) {
          return prev
        }
        const newTarget = remaining[0].id
        const updated = { ...prev }
        for (const field of MERGE_FIELDS) {
          if (updated[field.key] === id) {
            updated[field.key] = newTarget
          }
        }
        return updated
      })
    },
    [activeContacts],
  )

  const advance = useCallback(() => {
    const nextIndex = currentIndex + 1
    if (nextIndex >= duplicates.length) {
      onOpenChange(false)
      onComplete()
    } else {
      setCurrentIndex(nextIndex)
    }
  }, [currentIndex, duplicates.length, onOpenChange, onComplete])

  const handleSkip = useCallback(() => {
    advance()
  }, [advance])

  const handleMerge = useCallback(async () => {
    if (activeContacts.length < 2) {
      return
    }
    const target = activeContacts[0]
    const sources = activeContacts.slice(1)
    const fieldOverrides = buildFieldOverrides(
      fieldSelections,
      target.id,
      activeDetails.length > 0 ? activeDetails : activeContacts,
    )

    await mergeContacts(target.id, {
      sourceIds: sources.map((c) => c.id),
      fieldOverrides: Object.keys(fieldOverrides).length > 0 ? fieldOverrides : undefined,
      excludeAssociationIds: excludedAssociationIds.size > 0 ? Array.from(excludedAssociationIds) : undefined,
    })

    advance()
  }, [activeContacts, activeDetails, fieldSelections, excludedAssociationIds, mergeContacts, advance])

  if (!currentGroup) {
    return null
  }

  const canMerge = activeContacts.length >= 2 && !merging && !loadingDetails

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Review Duplicates ({currentIndex + 1} of {duplicates.length})
          </DialogTitle>
          <DialogDescription>
            {currentGroup.reason} — choose which field values and associations to keep, or skip this set.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          {/* Contact list with remove buttons */}
          <div className="space-y-1 pb-2">
            {activeContacts.map((c, i) => (
              <div
                key={c.id}
                className="flex items-center justify-between rounded-md border border-border/50 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Badge variant={i === 0 ? 'default' : 'secondary'} className="shrink-0">
                    {i === 0 ? 'Target' : 'Source'}
                  </Badge>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{contactDisplayName(c)}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {[c.email, c.organizationName].filter(Boolean).join(' \u2022 ') || 'No details'}
                    </div>
                  </div>
                </div>
                {activeContacts.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeContact(c.id)}
                    title="Remove from this duplicate set">
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {loadingDetails ? (
            <div className="py-4 text-center text-sm text-muted-foreground">Loading contact details...</div>
          ) : (
            activeContacts.length >= 2 && (
              <>
                <Separator />
                <div className="pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Fields
                </div>
                {MERGE_FIELDS.map((field) => (
                  <FieldSelector
                    key={field.key}
                    field={field}
                    contacts={activeDetails.length > 0 ? activeDetails : activeContacts}
                    selected={fieldSelections[field.key] ?? targetId}
                    onSelect={(id) => setFieldSelections((prev) => ({ ...prev, [field.key]: id }))}
                    namePrefix={`dup-${currentIndex}`}
                  />
                ))}

                {allAssociations.length > 0 && (
                  <>
                    <Separator className="my-2" />
                    <div className="pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Associations
                    </div>
                    <AssociationMergeSection
                      associations={allAssociations}
                      excludedIds={excludedAssociationIds}
                      onToggle={toggleAssociation}
                    />
                  </>
                )}
              </>
            )
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleSkip} disabled={merging}>
            Skip
          </Button>
          <Button onClick={handleMerge} disabled={!canMerge}>
            {merging ? 'Merging...' : `Merge ${activeContacts.length} Contacts`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
